import React from 'react';
import { useBranding } from '../../contexts/BrandingContext';

/**
 * GlobeMark — EazyCourseEngine Globe Logo SVG
 *
 * Based on DEV_HANDOVER_EazyCourseEngine_UI.md Section 3.
 *
 * Key properties:
 * - Blue var(--brand-primary) circle base
 * - Orange var(--brand-secondary) wave stripes (3 sinuous bands)
 * - Tilted 25° clockwise: transform="rotate(25,20,20)"
 * - Orange dashed flight-path arc over the top
 * - Everything clipped inside the circle — no color spill
 *
 * CRITICAL: Each instance must have a unique ID to prevent clipPath conflicts.
 * Pass a unique `id` prop for each instance on the same page.
 *
 * @param {Object} props
 * @param {string} props.id - Unique ID for clipPath (required for multiple instances)
 * @param {number} props.size - Size in pixels (default: 34)
 * @param {string} props.className - Additional CSS classes
 */
const GlobeMark = ({ id = 'globe', size = 34, className = '' }) => {
  const branding = useBranding(); // v2.37: Tenant-aware branding
  const clipId = `${id}-clip`;
  const maskId = `${id}-mask`;

  return (
    <svg
      viewBox="0 0 40 40"
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      className={className}
      aria-label={`${branding?.platform_name || '∑n-route'} Logo`}
      style={{ overflow: 'hidden' }}
    >
      <defs>
        {/* Clip path for the globe circle */}
        <clipPath id={clipId}>
          <circle cx="20" cy="20" r="17.5" />
        </clipPath>
        {/* Mask for extra safety - white = visible, black = hidden */}
        <mask id={maskId}>
          <circle cx="20" cy="20" r="17.5" fill="white" />
        </mask>
      </defs>

      {/* Globe container with both clip and mask for bulletproof containment */}
      <g clipPath={`url(#${clipId})`} mask={`url(#${maskId})`}>
        {/* Rotated content group */}
        <g transform="rotate(25,20,20)">
          {/* Blue circle base - slightly larger to prevent edge gaps */}
          <circle cx="20" cy="20" r="18" fill="var(--brand-primary)" />
          {/* Orange wave stripes (3 sinuous bands) */}
          <path
            d="M0 8 C8 4,12 12,20 8 C28 4,32 12,40 8 L40 14 C32 18,28 10,20 14 C12 18,8 10,0 14 Z"
            fill="var(--brand-secondary)"
          />
          <path
            d="M0 18 C8 14,12 22,20 18 C28 14,32 22,40 18 L40 24 C32 28,28 20,20 24 C12 28,8 20,0 24 Z"
            fill="var(--brand-secondary)"
          />
          <path
            d="M0 28 C8 24,12 32,20 28 C28 24,32 32,40 28 L40 34 C32 38,28 30,20 34 C12 38,8 30,0 34 Z"
            fill="var(--brand-secondary)"
          />
        </g>
      </g>

      {/* Clean circle border to hide any potential edge artifacts */}
      <circle
        cx="20"
        cy="20"
        r="17.5"
        fill="none"
        stroke="var(--brand-primary)"
        strokeWidth="1"
      />

      {/* Flight path arc (outside clip group — stays upright, not rotated) */}
      <path
        d="M8 32 Q12 4, 34 8"
        fill="none"
        stroke="var(--brand-secondary)"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeDasharray="1.5 1.5"
      />
      {/* Flight path endpoint dot */}
      <circle cx="33" cy="8" r="2" fill="var(--brand-secondary)" />
    </svg>
  );
};

export default GlobeMark;
